/**
 * Suspensions and the relationships console in the Admin (W9-REPUTATION, D478, ADR-0096) — framework-neutral view-models over the
 * `reputation-routes.ts` surface: who may do what (display only; the server re-authorizes every call), the stable refusals as copy
 * ids, and the small pure helpers the two screens share (the period of a suspension in the market's time zone, a catalogue label).
 * Neutral: no country, language or vocabulary literal lives here.
 */
import type { OccurrenceReasonView, OccurrenceStatusView, SuspensionStatusView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { formatMarketInstant } from './market-time.js';

export interface ReputationAuthority {
  readonly mayViewDrivers: boolean;
  readonly maySuspendDrivers: boolean;
  readonly mayViewRiders: boolean;
  readonly maySuspendRiders: boolean;
  readonly mayReadOccurrences: boolean;
  readonly mayRespond: boolean;
  readonly mayManageReasons: boolean;
  readonly mayViewRelationships: boolean;
  /** H1-RULES (D503, Q-136): the audited unblock of a block. */
  readonly mayUnblock: boolean;
}

/** Who may do what (display only — the server re-authorizes every call). `undefined` grants = unknown yet: show everything. */
export function reputationAuthority(grants: KnownGrants): ReputationAuthority {
  if (grants === undefined) return { mayViewDrivers: true, maySuspendDrivers: true, mayViewRiders: true, maySuspendRiders: true, mayReadOccurrences: true, mayRespond: true, mayManageReasons: true, mayViewRelationships: true, mayUnblock: true };
  const may = (permission: string): boolean => grantsAllow(grants, { permission: permission as never, target: 'GEO' });
  return {
    mayViewDrivers: may('driver:view'),
    maySuspendDrivers: may('driver:suspend'),
    mayViewRiders: may('rider:view'),
    maySuspendRiders: may('rider:suspend'),
    mayReadOccurrences: may('occurrence:read'),
    mayRespond: may('occurrence:respond'),
    mayManageReasons: may('occurrence:manage'),
    mayViewRelationships: may('relationship:view'),
    mayUnblock: may('relationship:unblock'),
  };
}

export type ReputationTone = 'neutral' | 'success' | 'warning' | 'danger';

export const SUSPENSION_STATUS_TONE: Readonly<Record<SuspensionStatusView, ReputationTone>> = { SCHEDULED: 'warning', ACTIVE: 'danger', ENDED: 'neutral', LIFTED: 'neutral' };
export const OCCURRENCE_STATUS_TONE: Readonly<Record<OccurrenceStatusView, ReputationTone>> = { NEW: 'warning', ANSWERED: 'success', CLOSED: 'neutral' };

/** The period of a suspension as the operator reads it: both ends in the market's time zone, or `—` for no end. */
export function suspensionPeriod(s: { readonly startsAt: string; readonly endsAt?: string }, timeZone: string | undefined, locale: string): { readonly from: string; readonly until: string | undefined } {
  return { from: formatMarketInstant(s.startsAt, timeZone, locale), until: s.endsAt !== undefined ? formatMarketInstant(s.endsAt, timeZone, locale) : undefined };
}

/**
 * The label of a catalogue reason in the console's language, then English, then any. A reason the catalogue does not list (or
 * one with no label at all) reads as `unknown` — the screen's translated "uncatalogued value" — never as its code.
 */
export function occurrenceReasonLabel(reasons: readonly OccurrenceReasonView[], code: string, lang: string, unknown: string): string {
  const r = reasons.find((x) => x.code === code);
  return r === undefined ? unknown : (r.labels[lang] ?? r.labels.en ?? Object.values(r.labels)[0] ?? unknown);
}

/** The country of a market id (`cc:slug` → `CC`), for the routes that are scoped by country. */
export function countryOfMarketId(marketId: string): string {
  return marketId.slice(0, 2).toUpperCase();
}
